import { CheckCircle2, Building2, Users, Settings, FileText, ListChecks, Scale, ExternalLink, MousePointerClick, UserCheck, Workflow } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';

interface OnboardingCompletionProps {
  companiesAdded: number;
  userAccessSkipped: boolean;
  generalSettingsModified: boolean;
  onFinish: () => void;
}

export function OnboardingCompletion({
  companiesAdded,
  userAccessSkipped,
  generalSettingsModified,
  onFinish,
}: OnboardingCompletionProps) {
  const navigate = useNavigate();

  const sections = [
    {
      icon: Building2,
      label: 'Company Setup',
      value: companiesAdded > 0 ? `${companiesAdded} ${companiesAdded === 1 ? 'company' : 'companies'} added` : 'No companies added',
      href: '/settings/company',
      status: companiesAdded > 0 ? 'done' : 'empty',
    },
    {
      icon: Users,
      label: 'User Access',
      value: userAccessSkipped ? 'Skipped — complete in User Management' : 'Access assigned',
      href: '/settings/users',
      status: userAccessSkipped ? 'skipped' : 'done',
    },
    {
      icon: Settings,
      label: 'General Settings',
      value: generalSettingsModified ? 'Settings saved' : 'Left as default',
      href: '/settings/general',
      status: 'done',
    },
    {
      icon: FileText,
      label: 'Templates',
      value: 'No templates set up — optional',
      href: '/settings/templates',
      status: 'optional',
    },
    {
      icon: ListChecks,
      label: 'Collection Strategies',
      value: 'No strategies set up — optional',
      href: '/settings/strategies',
      status: 'optional',
    },
    {
      icon: Scale,
      label: 'Dispute Types',
      value: 'No dispute types set up — optional',
      href: '/settings/dispute-types',
      status: 'optional',
    },
  ];

  const nextSteps = [
    {
      icon: Workflow,
      title: 'Assign Collection Strategies',
      description: 'Automate follow-up workflows for overdue accounts.',
    },
    {
      icon: UserCheck,
      title: 'Assign Credit Controllers',
      description: 'Set who is responsible for each customer account.',
    },
    {
      icon: MousePointerClick,
      title: 'Configure Collections',
      description: 'Create and manage collection cases to track outstanding balances.',
    },
  ];

  const statusColor = (status: string) => {
    if (status === 'done')     return 'text-green-600';
    if (status === 'skipped')  return 'text-amber-600';
    return 'text-muted-foreground';
  };

  return (
    <div className="flex flex-col h-full px-8 py-6 max-w-4xl mx-auto w-full">

      {/* Header — full width, centered */}
      <div className="flex flex-col items-center text-center mb-6">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600 mb-3">
          <CheckCircle2 className="h-7 w-7" />
        </div>
        <h2 className="text-xl font-semibold mb-1">You're all set!</h2>
        <p className="text-muted-foreground text-sm">
          Here's a summary of your setup. You can click any section to go back and make changes at any time.
        </p>
      </div>

      {/* Two-column body */}
      <div className="grid grid-cols-2 gap-6 flex-1 min-h-0">

        {/* Left — Setup summary */}
        <div className="flex flex-col min-h-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Setup summary</p>
          <div className="flex flex-col gap-2 overflow-y-auto">
            {sections.map(({ icon: Icon, label, value, href, status }) => (
              <Card key={label} className="shadow-none border shrink-0">
                <CardContent className="flex items-center justify-between py-2.5 px-3">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium leading-tight">{label}</p>
                      <p className={`text-xs leading-tight mt-0.5 ${statusColor(status)}`}>{value}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { onFinish(); navigate(href); }}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors shrink-0 ml-2"
                  >
                    Edit <ExternalLink className="h-3 w-3" />
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Right — What to do next + CTAs */}
        <div className="flex flex-col">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">What to do next</p>
          <p className="text-xs text-muted-foreground mb-3">
            Head to the <span className="font-medium text-foreground">Customers</span> page. Select one or more customers, then open the <span className="font-medium text-foreground">Actions</span> menu to:
          </p>

          <div className="flex flex-col gap-2 mb-6">
            {nextSteps.map(({ icon: Icon, title, description }) => (
              <div key={title} className="flex items-start gap-3 rounded-lg border bg-muted/30 px-3 py-2.5">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary mt-0.5">
                  <Icon className="h-3.5 w-3.5" />
                </div>
                <div>
                  <p className="text-sm font-medium leading-tight">{title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="flex flex-col gap-2.5">
            <Button
              size="lg"
              className="w-full"
              onClick={() => { onFinish(); navigate('/customers?from=onboarding'); }}
            >
              Go to Customers
            </Button>
            <button
              onClick={onFinish}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors text-center"
            >
              Go to Dashboard
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
